<template>
    <section
        v-if="blok"
        id="AirportTabs"
        v-editable="blok"
        :class="blok.tabStyle"
        data-test="airport-tabs"
        :style="tabStyles"
    >
        <div class="container">
            <h3 v-if="blok.title">{{ blok.title }}</h3>
            <div v-if="blok.subtitle" class="subtitle" v-html="blok.subtitle"></div>
            <div class="tabs">
                <button
                    :ref="
                        (el) => {
                            leftArrowBtn = el;
                        }
                    "
                    class="md:hidden tabs-arrow-btn tabs-arrow-btn--left"
                >
                    <NuxtIcon name="ri:arrow-left-s-line" class="z-10" />
                </button>
                <div
                    :ref="
                        (el) => {
                            tabControlEl = el;
                        }
                    "
                    class="tabs-control"
                >
                    <template v-for="tab in blok.tabs" :key="tab._uid">
                        <div
                            class="tab"
                            :class="{ active: currentSelected === tab._uid }"
                            @click="onTabClick(tab._uid)"
                        >
                            {{ tab.tab_name }}
                        </div>
                    </template>
                </div>
                <button
                    :ref="
                        (el) => {
                            rightArrowBtn = el;
                        }
                    "
                    class="md:hidden tabs-arrow-btn tabs-arrow-btn--right"
                >
                    <NuxtIcon name="ri:arrow-right-s-line" class="z-10" />
                </button>
            </div>

            <div v-if="blok.tabStyle === 'left-side-tabs'" class="mobile-tab-item">
                <select v-model="currentSelected" name="tabs-select">
                    <option v-for="tab in blok.tabs" :key="tab._uid" :value="tab._uid">
                        {{ tab.tab_name }}
                    </option>
                </select>
            </div>

            <div class="tab-content-container">
                <template v-for="tab in trimmedTabs" :key="tab._uid">
                    <div
                        v-show="currentSelected === tab._uid"
                        class="tab-content"
                        :class="{
                            'hide-content-tab': !readMore && !blok.showFullContent,
                            'full-content': blok.showFullContent,
                            active: currentSelected === tab._uid,
                        }"
                    >
                        <template v-if="tab.component === 'airport_tab_rich'">
                            <div class="rich-text-wrapper">
                                <rich-text :content="tab.content" />
                            </div>
                        </template>

                        <div v-else class="left" v-html="tab.content"></div>

                        <div v-if="tab.image && tab.image.filename" class="right">
                            <NuxtImg
                                provider="storyblok"
                                :src="tab.image.filename"
                                format="webp"
                                :title="tab.image?.title"
                                :alt="tab.image?.alt"
                                loading="lazy"
                                densities="x1"
                            />
                        </div>
                    </div>

                    <div
                        v-show="currentSelected === tab._uid && !blok.showFullContent"
                        class="tabs-content-btn md:hidden"
                    >
                        <button class="show-more-btn" @click="onClickReadMoreBtn">
                            <span v-if="!readMore" class="show-more-btn__text">Read more</span>
                            <span v-else class="show-more-btn__text">Read less</span>
                            <span class="show-more-btn__down">
                                <NuxtIcon v-if="readMore" name="ri:arrow-up-s-line" class="z-10" />
                                <NuxtIcon v-else name="ri:arrow-down-s-line" class="z-10" />
                            </span>
                        </button>
                    </div>
                </template>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import type { IAirportTab, IAirportTabRich, IAirportTabs } from '@nuxt-monorepo/ui/types/components/AirportTabs';
const props: any = defineProps<{ blok: IAirportTabs }>();

const currentSelected = ref(
    props.blok.tabs.find((tab: IAirportTab | IAirportTabRich) => tab.tab_name === props.blok.default_tab)?._uid ||
        props.blok.tabs[0]._uid
);

const tabControlEl = ref();
const leftArrowBtn = ref();
const rightArrowBtn = ref();
const readMore = ref(false);

const tabStyles = computed(() => {
    return {
        '--show-more-color': props.blok.hideButtonColor?.color || null,
        '--background-color': props.blok.BackgroundColor?.color || null,
        '--tab-title': props.blok.tabTitlesColor?.color || null,
        '--underline-color': props.blok.activeUnderlineColor?.color || null,
    };
});
const handleScroll = (e: any) => {
    if (e.target.scrollLeft !== 0 && !leftArrowBtn.value.classList.contains('is_active')) {
        leftArrowBtn.value.classList.add('is_active');
    }

    if (e.target.scrollLeft === 0) {
        leftArrowBtn.value.classList.remove('is_active');
    }

    if (
        Math.ceil(e.target.scrollLeft + e.target.offsetWidth) < e.target.scrollWidth &&
        !rightArrowBtn.value.classList.contains('is_active')
    ) {
        rightArrowBtn.value.classList.add('is_active');
    }

    if (Math.ceil(e.target.scrollLeft + e.target.offsetWidth) >= e.target.scrollWidth) {
        rightArrowBtn.value.classList.remove('is_active');
    }
};

const { render } = useStoryblokRichText({});
const richText = (content: any) => {
    return render(content.content);
};

const scrollToLeft = () => {
    tabControlEl.value.scrollTo({
        left: 0,
        behavior: 'smooth',
    });
};

const scrollToRight = () => {
    tabControlEl.value.scrollTo({
        left: tabControlEl.value.scrollWidth,
        behavior: 'smooth',
    });
};

const trimmedTabs = computed(() => {
    const tabs = JSON.parse(JSON.stringify(props.blok.tabs));
    tabs.forEach((tab) => {
        if (tab.component === 'airport_tab_rich') {
            return;
        }
    });
    return tabs;
});

const onClickReadMoreBtn = () => {
    readMore.value = !readMore.value;
};

const onTabClick = (tab: string) => {
    currentSelected.value = tab;
    readMore.value = false;
};

const onWindowResize = () => {
    if (tabControlEl.value.scrollWidth > window.innerWidth) {
        rightArrowBtn.value.classList.add('is_active');
    }
};

onMounted(() => {
    onWindowResize();

    leftArrowBtn.value?.addEventListener('click', scrollToLeft);
    rightArrowBtn.value?.addEventListener('click', scrollToRight);
    tabControlEl.value?.addEventListener('scroll', handleScroll);

    window.addEventListener('resize', onWindowResize);
});

onUnmounted(() => {
    leftArrowBtn.value?.removeEventListener('click', scrollToLeft);
    rightArrowBtn.value?.removeEventListener('click', scrollToRight);
    tabControlEl.value?.removeEventListener('scroll', handleScroll);

    window.removeEventListener('resize', onWindowResize);
});
</script>

<style scoped lang="postcss">
section {
    background: var(--background-color, none);
}
.container {
    @apply py-8;

    h3 {
        @apply text-[#343a40] font-normal text-center text-[40px] leading-[4rem] mb-6;
    }
}

.subtitle {
    @apply text-xl leading-[29px] text-[#343a40] font-normal text-center mb-12;
}

.tabs {
    @apply md:flex md:items-center md:justify-center block whitespace-nowrap relative md:whitespace-normal;

    .tabs-control {
        scrollbar-width: none;
        -ms-overflow-style: none;
        @apply overflow-auto text-center md:overflow-visible md:flex;

        &::-webkit-scrollbar {
            display: none;
        }
    }

    button {
        @apply absolute -translate-y-2/4 -translate-x-1/2 w-8 h-10 flex justify-center items-center text-base z-[1] border-[none] top-2/4 hidden;

        &.is_active {
            @apply block md:hidden;
        }
    }

    .tab {
        @apply mx-4 p-2 border-b border-gray-500 text-sm text-center cursor-pointer inline-block md:block md:text-lg;
        color: var(--tab-title, initial);

        &.active {
            @apply border-b-4 font-bold;
            border-color: var(--underline-color, theme('colors.primary.DEFAULT'));
        }
    }

    .tabs-arrow-btn {
        span {
            @apply w-6 h-6;
        }
        &::after {
            @apply content-[''] absolute top-0 h-full w-8 pointer-events-none;
        }

        &--left {
            @apply left-0;
            &::after {
                @apply right-0 z-[-1];
                background: linear-gradient(
                    to left,
                    rgba(255, 255, 255, 0) 0%,
                    rgb(255 255 255 / 30%) 10%,
                    rgb(255 255 255) 20%,
                    rgb(255 255 255) 100%
                );
                opacity: 0.5;
            }
        }

        &--right {
            @apply left-[100%];
            &::after {
                @apply left-0 z-[-1];
                background: linear-gradient(
                    to right,
                    rgba(255, 255, 255, 0) 0%,
                    rgb(255 255 255 / 30%) 10%,
                    rgb(255 255 255) 20%,
                    rgb(255 255 255) 100%
                );
                opacity: 0.5;
            }
        }
    }
}

.tab-content {
    @apply py-8 px-4 items-center overflow-hidden md:overflow-visible relative hidden;
    &.active {
        @apply block md:flex;
    }

    &.hide-content-tab {
        @apply h-[200px] md:h-auto;
    }

    &:not(.full-content):before {
        @apply content-[""] absolute h-[68px] md:h-0 w-full bottom-0;
        background: linear-gradient(180deg, rgba(248, 249, 250, 0) 46.35%, var(--background-color, #f8f9fa) 100%);
    }

    .left {
        @apply flex flex-col justify-center px-4 xl:px-0;

        :deep(p) {
            @apply text-[#343a40];

            a {
                @apply text-[#26a9e0];

                &:hover {
                    @apply underline;
                }
            }
        }
    }

    .right {
        @apply hidden lg:block min-w-[454px] ml-8;

        img {
            @apply h-[454px] w-full;
        }
    }

    :deep(h4) {
        @apply text-primary text-sm md:text-lg;
    }

    :deep(p) {
        @apply mb-6 text-base md:text-lg;

        &:last-child {
            @apply mb-0;
        }
    }
    :deep(ul) {
        @apply mb-4 pl-10 list-disc;
        &:last-child {
            @apply mb-0;
        }
        li {
            @apply text-base md:text-lg font-light md:font-normal text-[#212529];
        }
    }
    .rich-text-wrapper {
        white-space: initial;
    }
}

.tabs-content-btn {
    @apply text-center relative z-10 mt-6;

    .show-more-btn {
        @apply inline-flex flex-col items-center text-base font-semibold border-[none];
        color: var(--show-more-color, #0094ff);
    }
}

.left-side-tabs {
    .container {
        @apply md:flex m-auto md:flex-wrap;
        h3,
        .subtitle {
            @apply w-full;
        }
        .tab {
            @apply w-[170px] text-left relative;
            border-bottom-style: none;
        }
        .tabs {
            @apply max-md:hidden basis-[min-content];
            align-items: start;
            .tabs-control {
                @apply block;
            }
        }
        .tab-content-container {
            @apply pt-5 pb-8 px-6 border-2 border-solid border-[#000000] flex-1;
            .tabs-content-btn {
                @apply mt-10;
            }
        }
        .mobile-tab-item {
            @apply py-2;
            select {
                @apply w-full;
            }
        }
    }
}
.left-float-tabs {
    .tab-content {
        @apply pt-0;
        .rich-text-wrapper {
            @apply w-full;
            p:first-of-type {
                @apply mt-8;
            }
        }
    }
    .container {
        .tabs {
            @apply justify-start px-4;
            .tabs-control {
                @apply w-full md:gap-x-8 gap-x-6 border-b-[#bec1d2] border-b border-solid text-left flex;
            }
            .tab {
                @apply mx-0 px-0 border-0;
                &.active {
                    @apply border-b-secondary border-b-4 border-solid;
                    border-color: var(--underline-color, theme('colors.secondary.DEFAULT'));
                }
            }
        }
    }
}
</style>
